import { SearchState as SearchStateInterface } from '../types/resources';

/**
 * SearchState model class with utility methods
 */
export class SearchState {
  public searchTerm: string;
  public selectedCategories: string[];
  public currentPage: number;
  public pageSize: number;
  public sortBy: string;
  public sortOrder: 'asc' | 'desc';

  constructor(data?: Partial<SearchStateInterface>) {
    this.searchTerm = data?.searchTerm || '';
    this.selectedCategories = data?.selectedCategories || [];
    this.currentPage = data?.currentPage || 1;
    this.pageSize = data?.pageSize || 12;
    this.sortBy = data?.sortBy || 'updatedAt';
    this.sortOrder = data?.sortOrder || 'desc';
  }

  /**
   * Convert search state to query parameters for API calls
   */
  toQueryParams(): Record<string, any> {
    const params: Record<string, any> = {
      page: this.currentPage,
      limit: this.pageSize,
      sort: `${this.sortBy}:${this.sortOrder}`
    };

    if (this.searchTerm.trim()) {
      params.search = this.searchTerm.trim();
    }

    if (this.selectedCategories.length > 0) {
      params.categories = this.selectedCategories;
    }

    return params;
  }

  /**
   * Convert search state to URL search parameters
   */
  toURLSearchParams(): URLSearchParams {
    const params = new URLSearchParams();
    
    if (this.searchTerm.trim()) {
      params.set('search', this.searchTerm.trim());
    }
    
    if (this.selectedCategories.length > 0) {
      params.set('categories', this.selectedCategories.join(','));
    }
    
    if (this.currentPage > 1) {
      params.set('page', this.currentPage.toString());
    }
    
    if (this.pageSize !== 12) {
      params.set('limit', this.pageSize.toString());
    }
    
    if (this.sortBy !== 'updatedAt' || this.sortOrder !== 'desc') {
      params.set('sort', `${this.sortBy}:${this.sortOrder}`);
    }
    
    return params;
  }

  /**
   * Create SearchState from URL search parameters
   */
  static fromURLSearchParams(params: URLSearchParams): SearchState {
    const searchTerm = params.get('search') || '';
    const categoriesParam = params.get('categories');
    const selectedCategories = categoriesParam ? 
      categoriesParam.split(',').filter(cat => cat.trim() !== '') : [];
    
    // Validate and sanitize page number
    const pageParam = params.get('page');
    const currentPage = pageParam ? Math.max(1, parseInt(pageParam, 10)) || 1 : 1;
    
    // Validate and sanitize page size
    const limitParam = params.get('limit');
    let pageSize = 12; // default
    if (limitParam) {
      const parsedLimit = parseInt(limitParam, 10);
      if (!isNaN(parsedLimit) && parsedLimit > 0) {
        pageSize = Math.min(Math.max(6, parsedLimit), 48);
      }
    }
    
    const sortParam = params.get('sort') || 'updatedAt:desc';
    const [sortBy, sortOrder] = sortParam.split(':');

    return new SearchState({
      searchTerm: searchTerm.trim(),
      selectedCategories,
      currentPage,
      pageSize,
      sortBy: sortBy || 'updatedAt',
      sortOrder: (sortOrder === 'asc' || sortOrder === 'desc') ? sortOrder : 'desc'
    });
  }

  /**
   * Reset search state to defaults
   */
  reset(): SearchState {
    return new SearchState();
  }

  /**
   * Update search term and reset to first page
   */
  updateSearchTerm(searchTerm: string): SearchState {
    return new SearchState({
      ...this,
      searchTerm,
      currentPage: 1
    });
  }

  /**
   * Update selected categories and reset to first page
   */
  updateSelectedCategories(selectedCategories: string[]): SearchState {
    return new SearchState({
      ...this,
      selectedCategories,
      currentPage: 1
    });
  }

  /**
   * Update current page
   */
  updateCurrentPage(currentPage: number): SearchState {
    return new SearchState({
      ...this,
      currentPage
    });
  }

  /**
   * Update page size and reset to first page
   */
  updatePageSize(pageSize: number): SearchState {
    return new SearchState({
      ...this,
      pageSize,
      currentPage: 1
    });
  }

  /**
   * Update sort options and reset to first page
   */
  updateSort(sortBy: string, sortOrder: 'asc' | 'desc'): SearchState {
    return new SearchState({
      ...this,
      sortBy,
      sortOrder,
      currentPage: 1
    });
  }

  /**
   * Check if search state has active filters
   */
  hasActiveFilters(): boolean {
    return this.searchTerm.trim() !== '' || this.selectedCategories.length > 0;
  }

  /**
   * Get a copy of the current state
   */
  clone(): SearchState {
    return new SearchState(this);
  }

  /**
   * Generate a shareable URL for the current search state
   */
  getShareableUrl(baseUrl: string = '/resources'): string {
    const params = this.toURLSearchParams();
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  }

  /**
   * Check if the current state matches another search state
   */
  equals(other: SearchState): boolean {
    return (
      this.searchTerm === other.searchTerm &&
      this.selectedCategories.length === other.selectedCategories.length &&
      this.selectedCategories.every(cat => other.selectedCategories.includes(cat)) &&
      this.currentPage === other.currentPage &&
      this.pageSize === other.pageSize &&
      this.sortBy === other.sortBy &&
      this.sortOrder === other.sortOrder
    );
  }

  /**
   * Get a summary of the current search state for display
   */
  getSummary(): string {
    const parts: string[] = [];
    
    if (this.searchTerm) {
      parts.push(`searching for "${this.searchTerm}"`);
    }
    
    if (this.selectedCategories.length > 0) {
      parts.push(`in ${this.selectedCategories.length} categor${this.selectedCategories.length !== 1 ? 'ies' : 'y'}`);
    }
    
    if (this.currentPage > 1) {
      parts.push(`page ${this.currentPage}`);
    }
    
    return parts.length > 0 ? parts.join(', ') : 'all resources';
  }
}